import React, { useCallback, useEffect, useMemo } from 'react';
import { Avatar, Badge, Dropdown, Popover, Typography } from 'antd';
import {
  UserOutlined,
  LogoutOutlined,
  QuestionCircleOutlined,
  BarChartOutlined,
  DownOutlined,
  AppstoreOutlined,
  SwapOutlined,
  IdcardOutlined,
  PieChartOutlined,
  AuditOutlined,
  AlertOutlined,
} from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import usePermissions from '../../hooks/usePermissions';
import { useActiveAccountOptional } from '../../hooks/useActiveAccount';
import useAccountNavigate from '../../hooks/useAccountNavigate';
import useAllBags, { BAG_METRIC_LABELS } from '../../hooks/useAllBags';
import useNewIncidentsCount from '../../hooks/useNewIncidentsCount';
import useIncidentsAccess from '../../hooks/useIncidentsAccess';
import { progressColor, bagDisplayLabel } from '../../utils/bagStatus';
import { playNewIncidentSound, primeIncidentSound } from '../../utils/incidentSound';
import { showNewIncidentsToast } from '../../pages/Incidents/newIncidentToast';
import './nameDropdown.css';

const INCIDENTS_PATH = '/platform/incidents';

export default function NameDropdown() {
  const { user, logout, canAccessAllAccounts, userSub } = useAuth();
  const account = useActiveAccountOptional();
  const { accountPath } = useAccountNavigate();
  const { can } = usePermissions();
  // La API decide si esta persona ve incidentes (por su permiso o el de su rol).
  const { allowed: canViewIncidents } = useIncidentsAccess();
  const { Text } = Typography;

  const vm = useMemo(() => {
    const attrs = user?.attributes ?? {};
    const companyName = account?.clientInfo?.name || attrs.name || 'Nua Talker';
    const email = attrs.email || '';
    const hubspotId = account?.accountId || '';
    const avatarSrc = attrs.picture || attrs.profilePicture || undefined;
    return { companyName, email, hubspotId, avatarSrc };
  }, [account?.clientInfo?.name, account?.accountId, user]);

  const canUsers = can('platform.users');
  const canLimits = can('limits');
  const { bags } = useAllBags({
    clientId: canLimits ? vm.hubspotId : null,
    products: canLimits ? account?.clientInfo?.products : null,
  });
  const canManagePlatform = Boolean(canAccessAllAccounts);
  const canEditCurrentAccount = Boolean(canManagePlatform && vm.hubspotId);
  const navigate = useNavigate();

  // Incidente que aparece mientras se está en la plataforma: tarjeta + sonido, en
  // cualquier pantalla (el header está en todas), también en la de incidentes.
  const handleNewIncidents = useCallback(
    (incidents) => {
      playNewIncidentSound();
      showNewIncidentsToast({ incidents, onOpen: () => navigate(INCIDENTS_PATH) });
    },
    [navigate]
  );
  useEffect(() => {
    if (canViewIncidents) primeIncidentSound();
  }, [canViewIncidents]);

  // Incidentes nuevos desde la última vez que esta persona miró la pantalla de
  // incidentes: la bola roja. Solo se consulta a quien la API dice que puede verlos
  // (permiso «Incidentes» de la persona o de su rol); al resto ni se le consulta.
  const newIncidents =
    Number(
      useNewIncidentsCount({
        enabled: canViewIncidents,
        userSub: userSub || user?.user?.userId,
        onNew: handleNewIncidents,
      })
    ) || 0;

  const UserNameMenu = () => {
    if (!user) return [];
    const companyInfoContent = (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          padding: '8px 12px',
        }}
      >
        <Text style={{ fontWeight: 'bold', fontSize: '14px' }}>
          {vm.companyName || 'Empresa'}
        </Text>
        {!!vm.email && (
          <Text style={{ fontSize: '12px', color: '#666666' }}>
            {vm.email}
          </Text>
        )}
        {!!vm.hubspotId && (
          <Text style={{ fontSize: '12px', color: '#666666' }}>
            {vm.hubspotId}
          </Text>
        )}
      </div>
    );
    const balanceContent = (
      <div style={{ padding: '4px 4px', minWidth: 200 }}>
        {bags.map((bag) => {
          const label = bagDisplayLabel(bag, BAG_METRIC_LABELS);
          const remaining = Math.max(0, (bag.totalAmount ?? 0) - (bag.usedAmount ?? 0));
          const percent = Math.round(bag.usagePercent ?? 0);
          return (
            <div key={`${bag.productLabel}-${bag.bagId ?? bag.metric}`} style={{ marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Text style={{ fontSize: 11, fontWeight: 700, color: '#8c8c8c' }}>
                  {bag.productLabel} · {label}
                </Text>
                <span
                  style={{
                    minWidth: 15,
                    height: 15,
                    padding: '0 3px',
                    borderRadius: 8,
                    background: progressColor(bag.usagePercent ?? 0),
                    color: '#fff',
                    fontSize: 8,
                    fontWeight: 700,
                    lineHeight: '15px',
                    textAlign: 'center',
                  }}
                >
                  {percent}%
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                <span>Total</span>
                <Text strong style={{ fontSize: 12 }}>
                  {bag.totalAmount ?? 0}
                </Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                <span>Restante</span>
                <Text strong style={{ fontSize: 12, color: progressColor(bag.usagePercent ?? 0) }}>
                  {remaining}
                </Text>
              </div>
            </div>
          );
        })}
      </div>
    );

    return [
      {
        key: 'company',
        disabled: !canEditCurrentAccount,
        label: canEditCurrentAccount
          ? <Link to={`/accounts/${vm.hubspotId}`}>{companyInfoContent}</Link>
          : companyInfoContent,
      },
      { type: 'divider' },
      {
        label: (
          <Link to="/accounts">
            <SwapOutlined /> Cuentas
          </Link>
        ),
        key: 'switch-account',
      },
      canManagePlatform && {
        label: (
          <Link to="/products">
            <AppstoreOutlined /> Productos
          </Link>
        ),
        key: 'products',
      },
      canUsers && {
        label: (
          <Link to={accountPath('/users')}>
            <UserOutlined /> Usuarios
          </Link>
        ),
        key: 'users',
      },
      canLimits && {
        label:
          bags.length > 0 ? (
            <Popover
              content={balanceContent}
              trigger="hover"
              placement="left"
              mouseEnterDelay={0.2}
              align={{ offset: [-8, 0] }}
            >
              <Link to={accountPath('/limits')} style={{ display: 'block' }}>
                <PieChartOutlined /> Límites
              </Link>
            </Popover>
          ) : (
            <Link to={accountPath('/limits')}>
              <PieChartOutlined /> Límites
            </Link>
          ),
        key: 'limits',
      },
      canManagePlatform && {
        label: (
          <Link to="/consumption">
            <BarChartOutlined /> Consumo de Clientes
          </Link>
        ),
        key: 'consumption',
      },
      canManagePlatform && {
        label: (
          <Link to="/platform/audit">
            <AuditOutlined /> Auditoría
          </Link>
        ),
        key: 'audit',
      },
      canViewIncidents && {
        label: (
          <Link to={INCIDENTS_PATH}>
            <AlertOutlined /> Incidentes
            {newIncidents > 0 && (
              <Badge
                count={newIncidents}
                overflowCount={99}
                size="small"
                style={{ marginLeft: 8 }}
                data-testid="incidents-menu-badge"
              />
            )}
          </Link>
        ),
        key: 'incidents',
      },
      canManagePlatform && {
        label: (
          <Link to="/platform/profiles">
            <IdcardOutlined /> Administración de perfiles
          </Link>
        ),
        key: 'profiles',
      },
      { type: 'divider' },
      {
        label: (
          <a
            href="https://ceton.share.hsforms.com/2Me9blOAcSRKp2DVrbdv0Ww"
            target="_blank"
            rel="noreferrer"
          >
            <QuestionCircleOutlined /> Soporte
          </a>
        ),
        key: 'support',
      },
      {
        label: (
          <div onClick={logout}>
            <LogoutOutlined /> Salir
          </div>
        ),
        key: 'logout',
      },
    ].filter(Boolean);
  };

  return (
    <div className="container header headerAccountWrapper">
      <Dropdown
        overlayClassName="headerAccountDropdown"
        menu={{ items: UserNameMenu() }}
        trigger={['click']}
        placement="bottomRight"
      >
        <button
          type="button"
          className="headerAccountButton"
          aria-label={`Cuenta actual. HubSpot ID: ${vm.hubspotId || 'N/A'}${
            newIncidents > 0 ? `. ${newIncidents} incidentes nuevos` : ''
          }`}
        >
          <Badge count={newIncidents} overflowCount={99} size="small" offset={[-2, 2]}>
            <Avatar
              shape="square"
              icon={!vm.avatarSrc && <UserOutlined />}
              src={vm.avatarSrc}
              size={28}
            />
          </Badge>
          <span className="headerAccountTextBlock">
            <Text className="headerAccountName">{vm.companyName}</Text>
            {!!vm.hubspotId && (
              <span className="headerAccountSub">
                <span className="headerAccountHsValue">{vm.hubspotId}</span>
              </span>
            )}
          </span>
          <DownOutlined className="headerAccountChevron" />
        </button>
      </Dropdown>
    </div>
  );
}
